import type { Cents } from "../domain/money";
import type { LoanActivity } from "../domain/financing";
import {
  addDays,
  addMonth,
  computeLoanLedger,
  dayNumber,
  interestFor,
  originationCentsFor,
  type FacilityTerms,
  type LedgerEvent,
  type LoanLedger,
} from "./financing";

/**
 * CAPITAL PLANNING (spec §73).
 *
 * A FINANCIAL EVENT answers "when does money move?" — a different question
 * from the schedule's "when does work happen?". This engine DERIVES the
 * events from truths the application already holds (estimate, selected
 * quotes, actuals, CPM dates, financing terms, draws, rental assumptions,
 * the sale record) plus the explicitly-persisted decisions (payment plans,
 * planning start overrides). Nothing here is stored; run it again and the
 * same inputs give the same events.
 *
 * Permanent rules:
 *   • ACTUALS SUPERSEDE PROJECTIONS without double counting: projected
 *     renovation spend covers only the estimate REMAINING after recorded
 *     actuals; a funded draw replaces its planned twin; an actual sale
 *     replaces the forecast exit.
 *   • Payment timing is independent of task timing. Defaults are LABELLED
 *     assumptions; payment_plans rows are decisions and win.
 *   • Rental value is never called profit; flip ARV is expected exit
 *     revenue and carries scenario confidence.
 *   • PROFITABILITY AND LIQUIDITY ARE SEPARATE: peak external cash
 *     required is a first-class output.
 */

export type Confidence = "high" | "planning" | "scenario";

export interface FinancialEvent {
  /** Valuation at a forecast boundary; never a dated bill. */
  accrualOnly?: boolean;
  id: string;
  date: string;
  /** Positive = cash INTO the business, negative = cash out. */
  amountCents: Cents;
  kind: string;
  label: string;
  subject: { kind: "project" | "scenario" | "business"; id: string; name: string };
  facilityId: string | null;
  source: { kind: string; id: string | null };
  assumption: boolean;
  actual: boolean;
  confidence: Confidence;
  /** Existing category authority, carried only to decide permitted use of project cash. */
  costBucket?: string;
  status?: "expected" | "overdue" | "partial" | "paid" | "canceled" | "actual";
}

export interface DrawInput {
  id: string;
  facilityId: string;
  label: string;
  status: "planned" | "ready" | "requested" | "approved" | "funded" | "rejected";
  plannedDate: string | null;
  fundedDate: string | null;
  requestedCents: Cents | null;
  approvedCents: Cents | null;
  receivedCents: Cents | null;
  feeCents: Cents | null;
  taskId: string | null;
}

export interface PaymentPlanInput {
  id: string;
  label: string;
  direction: "in" | "out";
  sourceKind: "feature" | "quote" | "manual" | "facility" | "category";
  sourceId: string | null;
  pctX100: number | null;
  amountCents: Cents | null;
  anchor: "task-start" | "task-finish" | "date" | "project-start" | "project-finish";
  taskId: string | null;
  date: string | null;
  offsetDays: number;
}

export interface RentalInputs {
  monthlyRentCents: Cents | null;
  vacancyPctX100: number | null;
  propertyTaxAnnualCents: Cents | null;
  insuranceAnnualCents: Cents | null;
  hoaMonthlyCents: Cents | null;
  managementPctX100: number | null;
  maintenancePctX100: number | null;
  utilitiesMonthlyCents: Cents | null;
  otherMonthlyCents: Cents | null;
  leaseUpDays: number | null;
}

export interface FacilityInput extends FacilityTerms {
  refinanceLtvPctX100: number | null;
  refinanceClosingCostsCents: Cents | null;
  payoffFacilityId: string | null;
  expectedDate: string | null;
}

export interface ProjectPlanInputs {
  subject: { kind: "project" | "scenario" | "business"; id: string; name: string };
  strategy: "flip" | "rental";
  purchasePriceCents: Cents | null;
  acquisitionCostsCents: Cents;
  expectedArvCents: Cents | null;
  purchaseDate: string | null;
  scheduleStart: string | null;
  scheduleFinish: string | null;
  tasks: Array<{ id: string; name: string; start: string; finish: string; featureId: string | null }>;
  /**
   * Spec §75D: when the schedule performs a feature's work but no task is
   * LINKED to that feature, the execution forecast resolves the date
   * through the budget category both sides already agree on. Same rule for
   * one project and for the portfolio — there is no second timing model.
   */
  featureWorkFinish?: ReadonlyMap<string, string>;
  /**
   * Spec §75: when the exit is still a projection, the date the house is
   * expected to CLOSE — finishing the work, listing it and getting to the
   * closing table are three different days. A sale date somebody recorded
   * always wins; without this the plan assumes the house sells the day the
   * last nail goes in, which understates both interest and holding.
   */
  expectedExitDate?: string | null;
  /** Quote-adjusted working estimate, per feature (null featureId under ""). */
  estimateByFeature: ReadonlyMap<string, Cents>;
  /** Derived aggregate remainder; never an invented allocation of Actual. */
  remainingByFeature?: ReadonlyMap<string,Cents>;
  featureNames: ReadonlyMap<string, string>;
  /** Dated actual outflows from accounting, feature-attributed where known. */
  actualCosts: Array<{ date: string; amountCents: Cents; label: string; featureId: string | null; sourceKind: string; sourceId: string | null; cashAmountCents?: Cents; bucket?: string }>;
  facilities: FacilityInput[];
  draws: DrawInput[];
  paymentPlans: PaymentPlanInput[];
  loanActivities?: readonly LoanActivity[];
  /** Bounded source-derived commitments/equity events; no stored totals. */
  additionalEvents?: readonly FinancialEvent[];
  /** Work already dated by explicit commitments, after quote/category remap. */
  commitmentCoverage?: ReadonlyMap<string,Cents>;
  /** Explicit matches attribute unlinked costs for forecast netting only. */
  commitmentActualCoverage?: ReadonlyMap<string,Cents>;
  /**
   * Spec §74S: authorized-category adjustments — the signed difference an
   * authorized budget adds over (or removes from) the quote-adjusted
   * calculation for a category, dated where that category's work ends.
   * One event each, so the authorized figure counts exactly once.
   */
  categoryAdjustments?: Array<{ categoryId: string; label: string; deltaCents: Cents; date: string }>;
  rental: RentalInputs | null;
  sale: { saleDate: string | null; netProceedsCents: Cents; closedAt: string | null } | null;
  today: string;
  horizonEnd: string;
}

export interface ProjectPlan {
  subject: ProjectPlanInputs["subject"];
  strategy: "flip" | "rental";
  events: FinancialEvent[];
  ledgers: Map<string, LoanLedger>;
  /** Derived canonical ledger inputs for exact scenario re-evaluation; never persisted or posted. */
  loanInputs?: ReadonlyMap<string, readonly LedgerEvent[]>;
  assumptions: string[];
  /** Max cumulative net outflow of this project's own events. */
  peakCashRequiredCents: Cents;
  /** The project's own anchor date, used by planning offsets. */
  anchorDate: string | null;
  exitDate: string | null;
}

function push(
  events: FinancialEvent[],
  event: Omit<FinancialEvent, "id">,
): void {
  if (event.amountCents === 0) return;
  const identity = [event.subject.id, event.source.kind, event.source.id ?? event.label, event.kind, event.date];
  events.push({ ...event, id: identity.map(encodeURIComponent).join(":") });
}

/**
 * Everything a single project (or scenario) does to the bank account,
 * derived and dated.
 */
export function buildProjectPlan(inputs: ProjectPlanInputs): ProjectPlan {
  const events: FinancialEvent[] = [];
  const assumptions: string[] = [];
  const subject = inputs.subject;
  const confidenceFor = (assumed: boolean): Confidence =>
    subject.kind === "scenario" ? "scenario" : assumed ? "planning" : "high";

  /* ---------------- acquisition ---------------- */
  const closing =
    inputs.purchaseDate ??
    inputs.facilities.find((f) => f.closingDate)?.closingDate ??
    inputs.scheduleStart ??
    inputs.today;
  if (!inputs.purchaseDate) {
    assumptions.push("Closing date not recorded — planning uses the schedule start.");
  }
  const anchorDate = closing;

  if (inputs.purchasePriceCents !== null && inputs.purchasePriceCents > 0) {
    push(events, {
      date: closing, amountCents: -inputs.purchasePriceCents, kind: "acquisition",
      label: "Purchase price", subject, facilityId: null,
      source: { kind: "project", id: subject.id }, assumption: inputs.purchaseDate === null,
      actual: inputs.purchaseDate !== null && inputs.purchaseDate <= inputs.today,
      confidence: confidenceFor(inputs.purchaseDate === null),
    });
  }
  if (inputs.acquisitionCostsCents > 0) {
    push(events, {
      date: closing, amountCents: -inputs.acquisitionCostsCents, kind: "closing-costs",
      label: "Acquisition costs", subject, facilityId: null,
      source: { kind: "acquisition", id: null }, assumption: inputs.purchaseDate===null,
      actual: inputs.purchaseDate!==null && inputs.purchaseDate<=inputs.today, confidence: confidenceFor(inputs.purchaseDate===null),
    });
  }

  /* ---------------- financing: funding, fees, draws ---------------- */
  const ledgers = new Map<string, LoanLedger>();
  // Balance events collected per facility; every facility (refinance
  // included) is run through the ONE loan ledger at the end.
  const pendingLedgerEvents = new Map<string, LedgerEvent[]>();
  for (const facility of inputs.facilities) {
    const facilityClosing = facility.closingDate ?? closing;
    const ledgerEvents: LedgerEvent[] = [];
    pendingLedgerEvents.set(facility.id, ledgerEvents);
    for (const activity of inputs.loanActivities?.filter(a=>a.facilityId===facility.id) ?? []) {
      if (activity.principalCents) ledgerEvents.push({date:activity.date,kind:"principal-payment",amountCents:activity.principalCents,label:activity.id});
      if (activity.interestCents) ledgerEvents.push({date:activity.date,kind:"interest-payment",amountCents:activity.interestCents,label:activity.id,dueDate:activity.dueDate??undefined});
      if (activity.feeCents) ledgerEvents.push({date:activity.date,kind:"fee-payment",amountCents:activity.feeCents,label:activity.id});
      if (activity.principalCents) push(events,{date:activity.date,amountCents:-activity.principalCents,kind:"loan-principal-payment",label:`${facility.name} — principal`,subject,facilityId:facility.id,source:{kind:"loan-activity",id:activity.id},assumption:false,actual:true,confidence:"high"});
      // A linked expense already owns the interest/fees cash and cost. The
      // activity still settles the loan schedule without counting it twice.
      const linked=activity.expenseId && inputs.actualCosts.some(a=>a.sourceKind==="expense" && a.sourceId===activity.expenseId);
      if (!linked && activity.interestCents) push(events,{date:activity.date,amountCents:-activity.interestCents,kind:"loan-interest-actual",label:`${facility.name} — interest paid`,subject,facilityId:facility.id,source:{kind:"loan-activity",id:activity.id},assumption:false,actual:true,confidence:"high"});
      if (!linked && activity.feeCents) push(events,{date:activity.date,amountCents:-activity.feeCents,kind:"loan-fee-actual",label:`${facility.name} — fees paid`,subject,facilityId:facility.id,source:{kind:"loan-activity",id:activity.id},assumption:false,actual:true,confidence:"high"});
    }
    // A refinance facility's cash events (proceeds, closing costs) are
    // derived in the refinance block below, on its expected date.
    if (facility.loanType === "refinance") continue;

    if (facility.acquisitionFundingCents !== null && facility.acquisitionFundingCents > 0) {
      push(events, {
        date: facilityClosing, amountCents: facility.acquisitionFundingCents, kind: "lender-funding",
        label: `${facility.name} — acquisition funding`, subject, facilityId: facility.id,
        source: { kind: "facility", id: facility.id }, assumption: facility.closingDate === null,
        actual: facilityClosing <= inputs.today, confidence: confidenceFor(facility.closingDate === null),
      });
      ledgerEvents.push({ date: facilityClosing, kind: "draw", amountCents: facility.acquisitionFundingCents, label: "acquisition" });
    }
    const origination = originationCentsFor(facility);
    const paidFees=(inputs.loanActivities??[]).filter(a=>a.facilityId===facility.id && a.feeTarget==="origination").reduce((sum,a)=>sum+a.feeCents,0);
    if (origination.cents > 0) {
      push(events, {
        date: facilityClosing, amountCents: -Math.max(0,origination.cents-paidFees), kind: "loan-fees",
        label: `${facility.name} — origination & fees`, subject, facilityId: facility.id,
        source: { kind: "facility", id: facility.id }, assumption: false,
        actual: false, confidence: confidenceFor(false),
      });
    }

    // Draws: a FUNDED draw is fact; anything else is a plan on its date.
    for (const draw of inputs.draws.filter((d) => d.facilityId === facility.id && d.status !== "rejected")) {
      const funded = draw.status === "funded" && draw.fundedDate !== null && draw.receivedCents !== null;
      if (draw.status === "funded" && !funded) {
        assumptions.push(`${draw.label}: funded date or received amount is unknown — no actual cash or debt is inferred.`);
        continue;
      }
      const date = funded ? draw.fundedDate! : draw.plannedDate ?? inputs.scheduleFinish ?? inputs.horizonEnd;
      if (!funded && draw.plannedDate === null) {
        assumptions.push(`${draw.label}: no expected funding date — planned at the schedule finish.`);
      }
      const amount = funded
        ? draw.receivedCents!
        : (draw.approvedCents ?? draw.requestedCents ?? 0);
      if (amount > 0) {
        push(events, {
          date, amountCents: amount, kind: "lender-draw",
          label: `${facility.name} — ${draw.label}`, subject, facilityId: facility.id,
          source: { kind: "draw", id: draw.id }, assumption: !funded,
          actual: funded, confidence: funded ? "high" : confidenceFor(true),
        });
        ledgerEvents.push({ date, kind: "draw", amountCents: amount, label: draw.label });
      }
      const fee = draw.feeCents ?? facility.drawFeeCents;
      const recordedDrawFees=(inputs.loanActivities??[]).filter(a=>a.facilityId===facility.id && a.feeTarget==="draw" && a.drawId===draw.id).reduce((sum,a)=>sum+a.feeCents,0);
      if (fee !== null && fee > 0 && amount > 0) {
        push(events, {
          date, amountCents: -Math.max(0,fee-recordedDrawFees), kind: "draw-fee",
          label: `${facility.name} — draw fee`, subject, facilityId: facility.id,
          source: { kind: "draw", id: draw.id }, assumption: draw.feeCents === null,
          actual: funded && draw.feeCents !== null, confidence: funded ? "high" : confidenceFor(true),
        });
      }
    }

  }

  /* ---------------- renovation spend ---------------- */
  // Actuals first: dated, factual.
  const actualByFeature = new Map<string, Cents>();
  for (const actual of inputs.actualCosts) {
    push(events, {
      date: actual.date, amountCents: actual.cashAmountCents ?? -actual.amountCents, kind: `${actual.bucket ?? "renovation"}-actual`,
      label: actual.label, subject, facilityId: null,
      source: { kind: actual.sourceKind, id: actual.sourceId }, assumption: false,
      actual: true, confidence: "high",
    });
    if (actual.bucket && actual.bucket !== "renovation") continue;
    const key = actual.featureId ?? "";
    actualByFeature.set(key, (actualByFeature.get(key) ?? 0) + actual.amountCents);
  }

  // Projected REMAINING spend per feature (never double counted, spec §73Z).
  for(const [key,amount] of inputs.commitmentActualCoverage??[]){actualByFeature.set(key,(actualByFeature.get(key)??0)+amount);actualByFeature.set("",(actualByFeature.get("")??0)-amount);}
  const taskForFeature = new Map<string, { start: string; finish: string; name: string }>();
  for (const task of inputs.tasks) {
    if (task.featureId && !taskForFeature.has(task.featureId)) taskForFeature.set(task.featureId, task);
  }
  // Payment plans are keyed the same way the estimate map is: a feature by
  // its id, a selected quote by "quote:<id>" (the service enters quoted
  // amounts under that key so a quote's tranches can be scheduled too).
  const planBySource = new Map<string, PaymentPlanInput[]>();
  for (const plan of inputs.paymentPlans) {
    const key =
      plan.sourceKind === "feature" && plan.sourceId ? plan.sourceId
      : plan.sourceKind === "quote" && plan.sourceId ? `quote:${plan.sourceId}`
      : plan.sourceKind === "category" && plan.sourceId ? `category:${plan.sourceId}`
      : null;
    if (key === null) continue;
    const list = planBySource.get(key) ?? [];
    list.push(plan);
    planBySource.set(key, list);
  }
  const resolveAnchor = (plan: PaymentPlanInput): string => {
    const task = plan.taskId ? inputs.tasks.find((t) => t.id === plan.taskId) : null;
    const base =
      plan.anchor === "date" && plan.date ? plan.date
      : plan.anchor === "task-start" && task ? task.start
      : plan.anchor === "task-finish" && task ? task.finish
      : plan.anchor === "project-start" ? (inputs.scheduleStart ?? closing)
      : (inputs.scheduleFinish ?? inputs.horizonEnd);
    return addDays(base, plan.offsetDays);
  };

  for (const [featureId, estimate] of inputs.estimateByFeature) {
    const explicitRemaining=inputs.remainingByFeature?.get(featureId);
    if(explicitRemaining!==undefined&&(!Number.isSafeInteger(explicitRemaining)||explicitRemaining<0))throw new Error("Derived remaining cost must be nonnegative integer cents.");
    const remaining = Math.max(0, explicitRemaining===undefined ? estimate - (actualByFeature.get(featureId) ?? 0) - (inputs.commitmentCoverage?.get(featureId) ?? 0) : explicitRemaining-(inputs.commitmentCoverage?.get(featureId)??0)-(inputs.commitmentActualCoverage?.get(featureId)??0));
    if (remaining <= 0) continue;
    const name = inputs.featureNames.get(featureId) ?? "Project scope";
    const plans = planBySource.get(featureId) ?? [];
    if (plans.length > 0) {
      // A person defined the tranches: honour them exactly; a remainder
      // (percentages under 100) falls at the default with a label.
      let covered = 0;
      for (const plan of plans) {
        const amount = plan.amountCents ?? (plan.pctX100 !== null ? Math.round((remaining * plan.pctX100) / 10000) : 0);
        if (amount <= 0) continue;
        covered += amount;
        push(events, {
          date: resolveAnchor(plan), amountCents: -amount, kind: "renovation-payment",
          label: `${name} — ${plan.label}`, subject, facilityId: null,
          source: { kind: "payment-plan", id: plan.id }, assumption: false,
          actual: false, confidence: confidenceFor(false),
        });
      }
      const rest = remaining - covered;
      if (rest > 0) {
        const task = taskForFeature.get(featureId);
        push(events, {
          date: task?.finish ?? inputs.featureWorkFinish?.get(featureId) ?? inputs.scheduleFinish ?? inputs.horizonEnd,
          amountCents: -rest,
          kind: "renovation-payment", label: `${name} — remainder`, subject, facilityId: null,
          source: { kind: "feature", id: featureId || null }, assumption: true,
          actual: false, confidence: confidenceFor(true),
        });
      }
    } else {
      // DEFAULT ASSUMPTION (labelled): cost is paid when its work finishes;
      // scope with no linked task pays at the project finish.
      const task = taskForFeature.get(featureId);
      const date =
        task?.finish ?? inputs.featureWorkFinish?.get(featureId) ?? inputs.scheduleFinish ?? inputs.horizonEnd;
      push(events, {
        date, amountCents: -remaining, kind: "renovation-payment",
        label: task ? `${name} (at ${task.name} completion)` : `${name}`,
        subject, facilityId: null,
        source: { kind: "feature", id: featureId || null }, assumption: true,
        actual: false, confidence: confidenceFor(true),
      });
    }
  }
  if (inputs.estimateByFeature.size > 0) {
    assumptions.push("Unscheduled payments assume cost is due when the linked work completes.");
  }

  // Authorized category budgets adjust the projected renovation outflow by
  // their signed delta — once, dated where the category's work ends.
  for (const adjustment of inputs.categoryAdjustments ?? []) {
    push(events, {
      date: adjustment.date, amountCents: -adjustment.deltaCents, kind: "renovation-adjustment",
      label: adjustment.label, subject, facilityId: null,
      source: { kind: "category", id: adjustment.categoryId }, assumption: false,
      actual: false, confidence: confidenceFor(false),
    });
  }

  /* ---------------- exit ---------------- */
  let exitDate: string | null = null;
  if (inputs.strategy === "flip") {
    const actualSale = inputs.sale && inputs.sale.closedAt !== null;
    exitDate = inputs.sale?.saleDate ?? inputs.expectedExitDate ?? inputs.scheduleFinish ?? null;
    if (exitDate) {
      const proceeds = actualSale ? inputs.sale!.netProceedsCents : (inputs.expectedArvCents ?? 0);
      if (!actualSale && inputs.expectedArvCents === null) {
        assumptions.push("No expected sale price — the exit contributes nothing until ARV is set.");
      }
      if (proceeds > 0) {
        push(events, {
          date: exitDate, amountCents: proceeds, kind: "sale",
          label: actualSale ? "Sale — net proceeds" : "Expected sale (net of selling costs where recorded)",
          subject, facilityId: null, source: { kind: "sale", id: null },
          assumption: !actualSale, actual: !!actualSale,
          confidence: actualSale ? "high" : "scenario",
        });
      }
    } else {
      assumptions.push("No schedule finish and no sale date — no exit event projected.");
    }
  }

  /* ---------------- rental operations & refinance ---------------- */
  if (inputs.strategy === "rental" && inputs.rental) {
    const r = inputs.rental;
    const stabilized = inputs.scheduleFinish
      ? addDays(inputs.scheduleFinish, r.leaseUpDays ?? 0)
      : null;
    if (!stabilized && r.monthlyRentCents !== null) {
      assumptions.push("Rental income cannot be projected until the renovation has a scheduled finish.");
    }
    if (stabilized && r.monthlyRentCents !== null) {
      const vacancyFactor = 10000 - (r.vacancyPctX100 ?? 0);
      const effectiveRent = Math.round((r.monthlyRentCents * vacancyFactor) / 10000);
      const mgmt = r.managementPctX100 !== null ? Math.round((effectiveRent * r.managementPctX100) / 10000) : 0;
      const maint = r.maintenancePctX100 !== null ? Math.round((effectiveRent * r.maintenancePctX100) / 10000) : 0;
      const monthlyOpex =
        Math.round((r.propertyTaxAnnualCents ?? 0) / 12) +
        Math.round((r.insuranceAnnualCents ?? 0) / 12) +
        (r.hoaMonthlyCents ?? 0) + (r.utilitiesMonthlyCents ?? 0) + (r.otherMonthlyCents ?? 0) + mgmt + maint;
      let month = addMonth(stabilized);
      while (month <= inputs.horizonEnd) {
        push(events, {
          date: month, amountCents: effectiveRent, kind: "rental-income",
          label: "Rent (after vacancy)", subject, facilityId: null,
          source: { kind: "rental", id: null }, assumption: true, actual: false, confidence: "scenario",
        });
        if (monthlyOpex > 0) {
          push(events, {
            date: month, amountCents: -monthlyOpex, kind: "operating-expense",
            label: "Operating expenses", subject, facilityId: null,
            source: { kind: "rental", id: null }, assumption: true, actual: false, confidence: "scenario",
          });
        }
        month = addMonth(month);
      }
    }
  }

  // Refinance facilities: new principal in, closing costs out, payoff of
  // the named facility (handled through its ledger's payoff event below).
  const refinances = inputs.facilities.filter((f) => f.loanType === "refinance" && f.expectedDate);
  for (const refi of refinances) {
    const principal =
      refi.commitmentCents ??
      (refi.refinanceLtvPctX100 !== null && inputs.expectedArvCents !== null
        ? Math.round((inputs.expectedArvCents * refi.refinanceLtvPctX100) / 10000)
        : null);
    if (principal === null) {
      assumptions.push(`${refi.name}: neither a loan amount nor LTV × value is known — refinance not projected.`);
      continue;
    }
    push(events, {
      date: refi.expectedDate!, amountCents: principal, kind: "refinance-proceeds",
      label: `${refi.name} — proceeds`, subject, facilityId: refi.id,
      source: { kind: "facility", id: refi.id }, assumption: true, actual: false, confidence: "scenario",
    });
    // The new loan's balance starts at its principal so the ledger can
    // project the post-refinance debt service.
    pendingLedgerEvents.get(refi.id)?.push({ date: refi.expectedDate!, kind: "draw", amountCents: principal, label: "refinance principal" });
    if (refi.refinanceClosingCostsCents !== null && refi.refinanceClosingCostsCents > 0) {
      push(events, {
        date: refi.expectedDate!, amountCents: -refi.refinanceClosingCostsCents, kind: "refinance-costs",
        label: `${refi.name} — closing costs`, subject, facilityId: refi.id,
        source: { kind: "facility", id: refi.id }, assumption: false, actual: false, confidence: "scenario",
      });
    }
    if (!exitDate || refi.expectedDate! > exitDate) exitDate = refi.expectedDate!;
  }

  /* ---------------- loan ledgers: interest, payments, payoff ---------------- */
  for (const facility of inputs.facilities) {
    const ledgerEvents = pendingLedgerEvents.get(facility.id) ?? [];
    // Payoff: at the flip sale, at a refinance that names this facility,
    // or never within the horizon. A refinance facility itself is not
    // paid off by the exit unless another refinance names it.
    const refi = refinances.find((r) => r.payoffFacilityId === facility.id);
    const payoffDate =
      refi?.expectedDate ??
      (inputs.strategy === "flip" && facility.loanType !== "refinance" ? exitDate : null);
    if (payoffDate) ledgerEvents.push({ date: payoffDate, kind: "payoff", amountCents: 0, label: "payoff" });

    const ledger = computeLoanLedger({...facility,asOfDate:inputs.today}, ledgerEvents, inputs.horizonEnd);
    ledger.recordedFeesCents=(inputs.loanActivities??[]).filter(a=>a.facilityId===facility.id).reduce((sum,a)=>sum+a.feeCents,0);
    ledger.commitmentCents=facility.commitmentCents;
    ledger.revolving=facility.loanStructure === "revolving";
    ledgers.set(facility.id, ledger);
    assumptions.push(...ledger.assumptions);

    for (const payment of ledger.payments) {
      push(events, {
        date: payment.date, amountCents: -payment.interestCents, kind: "loan-payment",
        label: `${facility.name} — ${payment.accrualOnly ? "accrued interest (forecast boundary)" : "interest"}`, subject, facilityId: facility.id,
        accrualOnly: payment.accrualOnly,
        source: { kind: "facility", id: facility.id }, assumption: facility.interestRatePctX100 === null,
        actual: false, confidence: confidenceFor(false),
      });
      if (payment.principalCents) push(events,{date:payment.date,amountCents:-payment.principalCents,kind:"loan-principal-due",label:`${facility.name} — scheduled principal`,subject,facilityId:facility.id,source:{kind:"facility",id:facility.id},assumption:false,actual:false,confidence:confidenceFor(false)});
    }
    if (payoffDate) {
      // The ledger reports the exact principal the payoff retired —
      // including a draw funded that same day.
      if (ledger.payoffCents > 0) {
        push(events, {
          date: payoffDate, amountCents: -ledger.payoffCents, kind: "loan-payoff",
          label: `${facility.name} — principal payoff`, subject, facilityId: facility.id,
          source: { kind: "facility", id: facility.id }, assumption: false,
          actual: payoffDate <= inputs.today && inputs.sale != null && inputs.sale.closedAt !== null,
          confidence: confidenceFor(false),
        });
      }
    }
  }

  /* ---------------- ordering + per-project peak cash ---------------- */
  events.push(...(inputs.additionalEvents ?? []));
  events.sort(
    (a, b) =>
      a.date.localeCompare(b.date) || a.kind.localeCompare(b.kind) || a.label.localeCompare(b.label) || a.id.localeCompare(b.id),
  );
  // Peak cash is measured at END OF DAY: same-day flows are one
  // settlement (the payoff comes out of the closing that delivers the
  // sale proceeds; the lender funds the purchase it finances). An
  // intraday ordering artifact must never inflate the requirement.
  let running = 0;
  let minRunning = 0;
  let i = 0;
  while (i < events.length) {
    const date = events[i].date;
    while (i < events.length && events[i].date === date) {
      running += events[i].amountCents;
      i += 1;
    }
    minRunning = Math.min(minRunning, running);
  }

  return {
    subject,
    strategy: inputs.strategy,
    events,
    ledgers,
    loanInputs: pendingLedgerEvents,
    assumptions,
    peakCashRequiredCents: -minRunning,
    anchorDate,
    exitDate,
  };
}

/** Cumulative net position after each event, for the timeline view. */
export function runningNet(events: readonly FinancialEvent[]): Cents[] {
  let total = 0;
  return events.map((event) => (total += event.amountCents));
}

/* ------------------------------------------------------------------ *
 * Single-project financing views (spec §73.1)
 *
 * Everything below is a READING of the plan the engine above already
 * derived — no second loan calculator, no stored figure, no new math
 * beyond splitting the same events and ledger periods by "has this
 * actually happened".
 * ------------------------------------------------------------------ */

/** Event kinds that are financing COSTS (interest and fees). Principal
 *  moving in either direction is FUNDING, never a project cost. */
const FINANCING_COST_KINDS = new Set(["loan-payment", "loan-fees", "draw-fee", "refinance-costs"]);
const PRINCIPAL_KINDS = new Set(["lender-funding", "lender-draw", "loan-payoff", "refinance-proceeds"]);

export interface FacilitySummary {
  facilityId: string;
  name: string;
  /**
   * Principal actually outstanding: funded money in, minus actual payoff.
   * PLANNED draws are deliberately absent — a plan is not a balance.
   */
  currentPrincipalCents: Cents;
  undrawnCapacityCents?: Cents | null;
  /** The highest the ledger projects the balance to reach (planned included). */
  projectedPeakPrincipalCents: Cents;
  interestToDateCents: Cents;
  projectedFutureInterestCents: Cents;
  totalProjectedInterestCents: Cents;
  /** Origination, draw and refinance fees — projected and recorded alike. */
  feesCents: Cents;
  /** Interest + fees: what borrowing this money costs the project. */
  projectedFinancingCostCents: Cents;
  nextPayment: { date: string; amountCents: Cents } | null;
  paidOff: boolean;
}

export function summarizeFacilities(plan: ProjectPlan, today: string): FacilitySummary[] {
  const summaries: FacilitySummary[] = [];
  for (const [facilityId, ledger] of plan.ledgers) {
    const events = plan.events.filter((e) => e.facilityId === facilityId);
    let currentPrincipal = 0;
    let fundedPrincipal = 0;
    let paidOff = false;
    for (const event of events) {
      if (!event.actual || event.date>today) continue;
      if (event.kind === "lender-funding" || event.kind === "lender-draw" || event.kind === "refinance-proceeds") {
        currentPrincipal += event.amountCents;
        fundedPrincipal += event.amountCents;
      }
      if (event.kind === "loan-principal-payment") currentPrincipal += event.amountCents;
      if (event.kind === "loan-payoff") {
        currentPrincipal += event.amountCents; // negative: the payoff retired it
        paidOff = true;
      }
    }
    const name = events[0]?.label.split(" — ")[0] ?? facilityId;
    const interestToDate = ledger.payments
      .filter((p) => p.date <= today)
      .reduce((s, p) => s + p.interestCents + (p.paidInterestCents??0), 0);
    const fees = (ledger.recordedFeesCents??0) + events
      .filter((e) => e.kind === "loan-fees" || e.kind === "draw-fee" || e.kind === "refinance-costs")
      .reduce((s, e) => s + Math.abs(e.amountCents), 0);
    const next = ledger.payments.find((p) => !p.accrualOnly && p.date > today && (p.totalCents ?? p.interestCents) > 0) ?? null;
    summaries.push({
      facilityId,
      name,
      currentPrincipalCents: Math.max(0, currentPrincipal),
      undrawnCapacityCents: ledger.commitmentCents===null || ledger.commitmentCents===undefined ? null : Math.max(0,ledger.commitmentCents-(ledger.revolving ? currentPrincipal : fundedPrincipal)),
      projectedPeakPrincipalCents: ledger.peakBalanceCents,
      interestToDateCents: interestToDate,
      projectedFutureInterestCents: ledger.payments.filter(p=>p.date>today).reduce((sum,p)=>sum+p.interestCents,0),
      totalProjectedInterestCents: ledger.totalInterestCents,
      feesCents: fees,
      projectedFinancingCostCents: ledger.totalInterestCents + fees,
      nextPayment: next ? { date: next.date, amountCents: next.totalCents ?? next.interestCents } : null,
      paidOff,
    });
  }
  return summaries.sort((a, b) => a.name.localeCompare(b.name) || a.facilityId.localeCompare(b.facilityId));
}

/**
 * Financing cost the plan still expects AFTER today: future interest
 * payments and fee events not yet in the past. This is what the Phase 12
 * projector adds to recorded actuals — interest and fees ONLY; principal
 * is a funding source and never a project cost (spec §73.1H).
 */
export function projectedFutureFinancingCents(plan: ProjectPlan, today: string): Cents {
  let total = 0;
  for (const ledger of plan.ledgers.values()) {
    total += ledger.payments.filter((p) => p.date > today).reduce((s, p) => s + p.interestCents, 0);
  }
  for (const event of plan.events) {
    if (event.date > today && !event.actual && (event.kind === "loan-fees" || event.kind === "draw-fee" || event.kind === "refinance-costs")) {
      total += Math.abs(event.amountCents);
    }
  }
  return total;
}

export interface DrawTotals {
  fundedCents: Cents;
  /** Money still expected: planned/requested/approved, not yet funded. */
  plannedCents: Cents;
  /** Facility − funded − planned. Null when the facility size is unknown. */
  remainingCents: Cents | null;
}

export function drawTotals(
  rehabFacilityCents: Cents | null,
  draws: readonly DrawInput[],
): DrawTotals {
  let funded = 0;
  let planned = 0;
  for (const draw of draws) {
    if (draw.status === "rejected") continue;
    if (draw.status === "funded" && draw.fundedDate) funded += draw.receivedCents ?? 0;
    else planned += draw.approvedCents ?? draw.requestedCents ?? 0;
  }
  return {
    fundedCents: funded,
    plannedCents: planned,
    remainingCents: rehabFacilityCents === null ? null : rehabFacilityCents - funded - planned,
  };
}

export interface UpcomingMoney {
  events: FinancialEvent[];
  netCents: Cents;
  from: string;
  to: string;
}

/** The next `days` days of the plan — derived, never a stored list. */
export function upcomingMoney(plan: ProjectPlan, today: string, days = 30): UpcomingMoney {
  const to = addDays(today, days);
  const events = plan.events.filter((e) => e.date > today && e.date <= to);
  return { events, netCents: events.reduce((s, e) => s + e.amountCents, 0), from: today, to };
}

export interface CashPosition {
  /** The owner's own money already in: net of every ACTUAL event so far. */
  ownerCashInvestedCents: Cents;
  currentDebtCents: Cents;
  projectedPeakDebtCents: Cents;
  projectedFinancingCostCents: Cents;
  /** The most owner cash this project ever needs, start to exit. */
  peakOwnerCashRequiredCents: Cents;
  nextPayment: { date: string; amountCents: Cents; facilityName: string } | null;
}

export function cashPosition(plan: ProjectPlan, today: string): CashPosition {
  const summaries = summarizeFacilities(plan, today);
  const actualNet = plan.events.filter((e) => e.actual && e.date<=today).reduce((s, e) => s + e.amountCents, 0);
  const next = summaries
    .filter((s) => s.nextPayment !== null)
    .sort((a, b) => a.nextPayment!.date.localeCompare(b.nextPayment!.date))[0];
  return {
    ownerCashInvestedCents: Math.max(0, -actualNet),
    currentDebtCents: summaries.reduce((s, f) => s + f.currentPrincipalCents, 0),
    projectedPeakDebtCents: summaries.reduce((s, f) => s + f.projectedPeakPrincipalCents, 0),
    projectedFinancingCostCents: summaries.reduce((s, f) => s + f.projectedFinancingCostCents, 0),
    peakOwnerCashRequiredCents: plan.peakCashRequiredCents,
    nextPayment: next
      ? { date: next.nextPayment!.date, amountCents: next.nextPayment!.amountCents, facilityName: next.name }
      : null,
  };
}

export interface RefinanceTransition {
  date: string;
  payoffCents: Cents;
  proceedsCents: Cents;
  costsCents: Cents;
  /** Proceeds − payoff − costs. Negative = the refinance needs cash. */
  netCapitalReturnedCents: Cents;
  /** Owner cash the project needed before the refinance returned any. */
  cashInvestedCents: Cents;
  cashLeftInDealCents: Cents;
}

/** The rehab-loan → mortgage moment, in the four numbers that matter. */
export function refinanceTransition(plan: ProjectPlan): RefinanceTransition | null {
  const proceeds = plan.events.find((e) => e.kind === "refinance-proceeds");
  if (!proceeds) return null;
  const date = proceeds.date;
  const payoff = plan.events
    .filter((e) => e.kind === "loan-payoff" && e.date === date)
    .reduce((s, e) => s + Math.abs(e.amountCents), 0);
  const costs = plan.events
    .filter((e) => e.kind === "refinance-costs" && e.date === date)
    .reduce((s, e) => s + Math.abs(e.amountCents), 0);
  const returned = proceeds.amountCents - payoff - costs;
  const invested = plan.peakCashRequiredCents;
  return {
    date,
    payoffCents: payoff,
    proceedsCents: proceeds.amountCents,
    costsCents: costs,
    netCapitalReturnedCents: returned,
    cashInvestedCents: invested,
    cashLeftInDealCents: Math.max(0, invested - Math.max(0, returned)),
  };
}

/** Guard: these sets must stay disjoint — a kind is a cost or funding, never both. */
export function financingKindsAreDisjoint(): boolean {
  for (const kind of FINANCING_COST_KINDS) if (PRINCIPAL_KINDS.has(kind)) return false;
  return true;
}

/**
 * The stabilized rental economics DERIVED from a plan: debt and its
 * monthly service come off the plan's own ledgers at the horizon, and
 * cash invested is the plan's peak owner-cash requirement. One derivation,
 * used by the service and the screen alike.
 */
export function stabilizedRentalEconomics(
  plan: ProjectPlan,
  rental: RentalInputs,
  estimatedValueCents: Cents | null,
): RentalEconomics {
  let debt = 0;
  let monthlyDebtService = 0;
  for (const ledger of plan.ledgers.values()) {
    debt += ledger.endingBalanceCents;
    // The last FULL monthly interest collection of a loan still open at
    // the horizon is the stabilized debt service (interest-only
    // facilities). The horizon end usually cuts a period short, and that
    // few-day stub is NOT a month's payment — found live, where a 6-day
    // stub understated debt service five-fold.
    if (ledger.endingBalanceCents > 0) {
      const fullMonth = ledger.payments.filter((p) => dayNumber(p.to) - dayNumber(p.from) >= 28);
      const last = fullMonth[fullMonth.length - 1] ?? ledger.payments[ledger.payments.length - 1];
      if (last) monthlyDebtService += last.interestCents;
    }
  }
  return rentalEconomics({
    rental,
    estimatedValueCents,
    debtCents: debt,
    monthlyDebtServiceCents: monthlyDebtService,
    cashInvestedCents: plan.peakCashRequiredCents > 0 ? plan.peakCashRequiredCents : null,
  });
}

/* ------------------------------------------------------------------ *
 * Portfolio combine (spec §73N/O/P)
 * ------------------------------------------------------------------ */

export interface CapitalInputs {
  cashCents: Cents;
  loc: { limitCents: Cents; balanceCents: Cents; aprPctX100: number | null } | null;
}

export interface PortfolioPoint {
  date: string;
  cashCents: Cents;
  locBalanceCents: Cents;
  debtCents: Cents;
  events: FinancialEvent[];
}

export interface PortfolioResult {
  points: PortfolioPoint[];
  peakExternalCashRequiredCents: Cents;
  lowestLiquidityCents: Cents;
  lowestLiquidityDate: string | null;
  locPeakCents: Cents;
  locInterestCents: Cents;
  totalInterestCents: Cents;
  completedWithinHorizon: number;
  netPositionChangeCents: Cents;
  perSubject: Array<{
    subject: ProjectPlan["subject"];
    peakCashRequiredCents: Cents;
    exitDate: string | null;
    netCents: Cents;
  }>;
  reserveViolations: Array<{ date: string; cashCents: Cents }>;
  assumptions: string[];
}

/** Shift every event of a plan so its anchor lands on `startDate`. */
export function shiftPlan(plan: ProjectPlan, startDate: string): ProjectPlan {
  if (!plan.anchorDate || plan.anchorDate === startDate) return plan;
  const delta = dayNumber(startDate) - dayNumber(plan.anchorDate);
  return {
    ...plan,
    anchorDate: startDate,
    exitDate: plan.exitDate ? addDays(plan.exitDate, delta) : null,
    events: plan.events.map((event) => ({ ...event, date: addDays(event.date, delta) })),
  };
}

export interface ConservativeAssumptions {
  overrunPctX100: number;
  durationPctX100: number;
  drawDelayDays: number;
  saleDelayDays: number;
}

/** The Conservative transform (spec §73U): explicit, user-configured. */
export function applyConservative(plan: ProjectPlan, c: ConservativeAssumptions): ProjectPlan {
  const anchor = plan.anchorDate;
  const stretch = (date: string): string => {
    if (!anchor) return date;
    const offset = dayNumber(date) - dayNumber(anchor);
    if (offset <= 0) return date;
    return addDays(anchor, Math.round((offset * (10000 + c.durationPctX100)) / 10000));
  };
  return {
    ...plan,
    exitDate: plan.exitDate ? addDays(stretch(plan.exitDate), c.saleDelayDays) : null,
    events: plan.events.map((event) => {
      let date = event.actual ? event.date : stretch(event.date);
      let amount = event.amountCents;
      if (!event.actual) {
        if (event.kind === "renovation-payment") {
          amount = Math.round((amount * (10000 + c.overrunPctX100)) / 10000);
        }
        if (event.kind === "lender-draw") date = addDays(date, c.drawDelayDays);
        if (event.kind === "sale" || event.kind === "refinance-proceeds") date = addDays(date, c.saleDelayDays);
      }
      return { ...event, date, amountCents: amount };
    }),
  };
}

/**
 * Merge every plan against the business's capital. Cash shortfalls draw
 * the LOC automatically (labelled), and what the LOC cannot cover is the
 * PEAK EXTERNAL CASH REQUIRED — the number that says whether the plan is
 * executable at all.
 */
export function combinePortfolio(
  plans: readonly ProjectPlan[],
  capital: CapitalInputs,
  horizonEnd: string,
  minReserveCents: Cents = 0,
  businessPlan?: ProjectPlan,
): PortfolioResult {
  const allPlans=businessPlan?[...plans,businessPlan]:plans;
  const assumptions = allPlans.flatMap((p) => p.assumptions.map((a) => `${p.subject.name}: ${a}`));
  const all = allPlans.flatMap((p) => p.events)
    .filter((e) => e.date <= horizonEnd)
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.subject.id.localeCompare(b.subject.id) ||
        a.kind.localeCompare(b.kind) ||
        a.id.localeCompare(b.id),
    );

  let cash = capital.cashCents;
  let loc = capital.loc ? capital.loc.balanceCents : 0;
  const locLimit = capital.loc?.limitCents ?? 0;
  let locPeak = loc;
  let locInterest = 0;
  let external = 0;
  let lowest = cash;
  let lowestDate: string | null = null;
  const points: PortfolioPoint[] = [];
  const reserveViolations: Array<{ date: string; cashCents: Cents }> = [];
  const byDate = new Map<string, FinancialEvent[]>();
  for (const event of all) {
    const list = byDate.get(event.date) ?? [];
    list.push(event);
    byDate.set(event.date, list);
  }
  const dates = [...byDate.keys()].sort();
  let lastDate: string | null = null;
  for (const date of dates) {
    // LOC interest accrues between event dates on the running balance.
    if (capital.loc && capital.loc.aprPctX100 !== null && lastDate && loc > 0) {
      const days = dayNumber(date) - dayNumber(lastDate);
      const interest = interestFor(loc, capital.loc.aprPctX100, days);
      locInterest += interest;
      cash -= interest;
    }
    for (const event of byDate.get(date)!) cash += event.amountCents;
    // Bridge: cover a shortfall from the LOC; repay the LOC from surplus.
    if (cash < 0 && capital.loc && loc < locLimit) {
      const draw = Math.min(locLimit - loc, -cash);
      loc += draw;
      cash += draw;
    }
    if (cash > 0 && loc > 0) {
      const repay = Math.min(cash, loc);
      loc -= repay;
      cash -= repay;
    }
    if (cash < 0) {
      external = Math.max(external, -cash);
    }
    locPeak = Math.max(locPeak, loc);
    if (cash < lowest) {
      lowest = cash;
      lowestDate = date;
    }
    if (cash < minReserveCents) {
      reserveViolations.push({ date, cashCents: cash });
    }
    const debt = allPlans.reduce((sum, p) => {
      let total = 0;
      for (const ledger of p.ledgers.values()) {
        const period = ledger.periods.filter((x) => x.from <= date).slice(-1)[0];
        const last=ledger.periods.at(-1);
        total += last && date>=last.to ? ledger.endingBalanceCents : period ? period.balanceCents : 0;
      }
      return sum + total;
    }, 0);
    points.push({ date, cashCents: cash, locBalanceCents: loc, debtCents: debt + loc, events: byDate.get(date)! });
    lastDate = date;
  }

  const totalInterest =
    allPlans.reduce((sum, p) => sum + [...p.ledgers.values()].reduce((s, l) => s + l.totalInterestCents, 0), 0) +
    locInterest;

  return {
    points,
    peakExternalCashRequiredCents: external,
    lowestLiquidityCents: lowest,
    lowestLiquidityDate: lowestDate,
    locPeakCents: locPeak,
    locInterestCents: locInterest,
    totalInterestCents: totalInterest,
    completedWithinHorizon: plans.filter((p) => p.exitDate !== null && p.exitDate <= horizonEnd).length,
    netPositionChangeCents: cash - capital.cashCents + (capital.loc ? capital.loc.balanceCents - loc : 0),
    perSubject: plans.map((p) => ({
      subject: p.subject,
      peakCashRequiredCents: p.peakCashRequiredCents,
      exitDate: p.exitDate,
      netCents: p.events.reduce((s, e) => s + e.amountCents, 0),
    })),
    reserveViolations,
    assumptions,
  };
}

/* ------------------------------------------------------------------ *
 * Rental economics (spec §73B/L/M) — headline metrics, not events
 * ------------------------------------------------------------------ */

export interface RentalEconomics {
  effectiveMonthlyRentCents: Cents;
  monthlyOperatingCents: Cents;
  monthlyNoiCents: Cents;
  annualNoiCents: Cents;
  monthlyDebtServiceCents: Cents;
  monthlyCashFlowCents: Cents;
  annualCashFlowCents: Cents;
  capRatePctX100: number | null;
  dscrX100: number | null;
  cashOnCashPctX100: number | null;
  /** Estimated value − debt. NEVER called profit. */
  equityCents: Cents | null;
  cashInvestedCents: Cents | null;
  assumptions: string[];
}

export function rentalEconomics(inputs: {
  rental: RentalInputs;
  estimatedValueCents: Cents | null;
  debtCents: Cents;
  monthlyDebtServiceCents: Cents;
  cashInvestedCents: Cents | null;
}): RentalEconomics {
  const r = inputs.rental;
  const assumptions: string[] = [];
  const rent = r.monthlyRentCents ?? 0;
  if (r.monthlyRentCents === null) assumptions.push("Monthly rent not set.");
  const effective = Math.round((rent * (10000 - (r.vacancyPctX100 ?? 0))) / 10000);
  const mgmt = r.managementPctX100 !== null ? Math.round((effective * r.managementPctX100) / 10000) : 0;
  const maint = r.maintenancePctX100 !== null ? Math.round((effective * r.maintenancePctX100) / 10000) : 0;
  const operating =
    Math.round((r.propertyTaxAnnualCents ?? 0) / 12) + Math.round((r.insuranceAnnualCents ?? 0) / 12) +
    (r.hoaMonthlyCents ?? 0) + (r.utilitiesMonthlyCents ?? 0) + (r.otherMonthlyCents ?? 0) + mgmt + maint;
  const noi = effective - operating;
  const cashFlow = noi - inputs.monthlyDebtServiceCents;
  const value = inputs.estimatedValueCents;
  return {
    effectiveMonthlyRentCents: effective,
    monthlyOperatingCents: operating,
    monthlyNoiCents: noi,
    annualNoiCents: noi * 12,
    monthlyDebtServiceCents: inputs.monthlyDebtServiceCents,
    monthlyCashFlowCents: cashFlow,
    annualCashFlowCents: cashFlow * 12,
    capRatePctX100: value && value > 0 ? Math.round((noi * 12 * 10000) / value) : null,
    dscrX100:
      inputs.monthlyDebtServiceCents > 0 ? Math.round((noi * 100) / inputs.monthlyDebtServiceCents) : null,
    cashOnCashPctX100:
      inputs.cashInvestedCents && inputs.cashInvestedCents > 0
        ? Math.round((cashFlow * 12 * 10000) / inputs.cashInvestedCents)
        : null,
    equityCents: value !== null ? value - inputs.debtCents : null,
    cashInvestedCents: inputs.cashInvestedCents,
    assumptions,
  };
}
